import { List } from 'immutable'
import type { ReactElement } from 'react'
import { partition } from 'lodash-es'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import cc from 'classcat'

import { Tooltip } from '../Tooltip'
import { switchLanguage } from '../../store/actions'
import { useTranslate, withComponents } from '../../utils/translate'
import SettingsLayer from '../templateComponents/Workspace/SettingsLayer'
import storage from '../../utils/localStorage'

export const lsKey = 'content-editing-locale'

type Props = {
  active: boolean | string
  onToggle: ({ submit }?: { submit?: boolean }) => void
}

export default function LanguageSwitch({ active, onToggle }: Readonly<Props>): ReactElement {
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))
  const shopBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'locationBase', 'origin']))
  const defaultLocale = useSelector<State, string>((state) => state.getIn(['shop', 'defaultLocale']))
  const selectedShopLocale = useSelector<State, string>((state) => state.getIn(['shop', 'locale']))
  const availableLocales = useSelector<State, Immutable.List<Core.Locale>>((state) =>
    state.getIn(['shop', 'locales'], List()),
  ).toJS()
  const virtualRef = useRef<HTMLElement>(null)
  const [autoTranslatableLocales, nonAutoTranslatableLocales] = partition(
    availableLocales.filter((locale) => locale.identifier !== defaultLocale),
    'isAutoTranslationEnabled',
  )

  const dispatch = useDispatch<GlobalDispatch>()

  const t = useTranslate('interface', 'components.editorLanguageSwitchComponent')
  const location = useLocation()

  const handleSwitchLanguage = (locale: string) => {
    storage.setItem(lsKey, locale)

    if (selectedShopLocale !== locale) {
      dispatch(switchLanguage(locale, location))
    } else {
      onToggle()
    }
  }

  useEffect(() => {
    if (!storage.getItem(lsKey)) onToggle()
    // We only want this to trigger onMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeClass = active
    ? 'ep-bottom-bar-button-language-switch-arrow-up'
    : 'ep-bottom-bar-button-language-switch-arrow-down'

  const iconClassname = cc([
    'ep-bottom-bar-button',
    'ep-bottom-bar-button-language-switch',
    {
      checked: active,
    },
  ])

  return (
    <>
      <span className="ep-bottom-bar-language-switch" title={t('switchLanguageModal.title')} onClick={() => onToggle()}>
        <span className={iconClassname} />
        <span className="ep-bottom-bar-language-switch-text" ref={virtualRef}>
          {t(`:enumerations.languages.${selectedShopLocale.substring(0, 2)}`)}
        </span>
        <span className={cc(['ep-bottom-bar-language-switch-arrow', activeClass])} />
      </span>

      <SettingsLayer
        open={!!active}
        side="top"
        virtualRef={virtualRef}
        className="ep-settingslayer-language-switch"
        onEscapeKeyDown={() => onToggle()}
        hideArrow={false}
        draggable={false}
        align="center"
        sideOffset={30}
      >
        {({ renderLayout }) => (
          <>
            {renderLayout({
              body: renderBody(),
              footer: <footer />,
            })}
          </>
        )}
      </SettingsLayer>
    </>
  )

  function renderBody() {
    return (
      <div className="ep-settingslayer-language">
        <strong>{t('switchLanguageModal.sourceLanguageTitle')}</strong>
        <button
          onClick={() => handleSwitchLanguage(defaultLocale)}
          data-testid={`language-switch-button-${defaultLocale.substring(0, 2)}`}
          className={cc([
            'ep-settingslayer-default-language',
            { 'ep-settingslayer-language-switch-item-selected': defaultLocale === selectedShopLocale },
          ])}
        >
          <svg
            fill="#000000"
            width="13"
            height="13"
            viewBox="0 0 32 32"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              <path d="M32.032 16c0-8.501-6.677-15.472-15.072-15.969-0.173-0.019-0.346-0.032-0.523-0.032-0.052 0-0.104 0.005-0.156 0.007-0.093-0.002-0.186-0.007-0.281-0.007-8.84 0-16.032 7.178-16.032 16.001s7.192 16.001 16.032 16.001c0.094 0 0.188-0.006 0.281-0.008 0.052 0.002 0.104 0.008 0.156 0.008 0.176 0 0.349-0.012 0.523-0.032 8.395-0.497 15.072-7.468 15.072-15.969zM29.049 21.151c-0.551-0.16-1.935-0.507-4.377-0.794 0.202-1.381 0.313-2.84 0.313-4.357 0-1.196-0.069-2.354-0.197-3.469 3.094-0.37 4.45-0.835 4.54-0.867l-0.372-1.050c0.695 1.659 1.080 3.478 1.080 5.386 0 1.818-0.352 3.555-0.987 5.151zM8.921 16c0-1.119 0.074-2.212 0.21-3.263 1.621 0.127 3.561 0.222 5.839 0.243v6.939c-2.219 0.021-4.114 0.111-5.709 0.234-0.22-1.319-0.34-2.715-0.34-4.154zM16.967 2.132c2.452 0.711 4.552 4.115 5.492 8.628-1.512 0.12-3.332 0.209-5.492 0.229v-8.857zM14.971 2.156v8.832c-2.136-0.021-3.965-0.109-5.502-0.226 0.96-4.457 3.076-7.836 5.502-8.606zM14.971 21.913l0 7.929c-2.263-0.718-4.256-3.705-5.293-7.719 1.492-0.11 3.253-0.189 5.292-0.21zM16.967 29.868l-0-7.955c2.061 0.020 3.814 0.102 5.288 0.217-1.019 4.067-3 7.076-5.288 7.738zM16.967 19.92l0-6.939c2.291-0.021 4.218-0.118 5.818-0.25 0.131 1.053 0.203 2.147 0.203 3.268 0 1.442-0.116 2.84-0.329 4.16-1.575-0.128-3.462-0.219-5.692-0.24zM28.588 9.81c-0.302 0.094-1.564 0.453-4.094 0.751-0.564-2.998-1.584-5.561-2.91-7.412 3.048 1.325 5.535 3.697 7.005 6.661zM11.213 2.831c-1.632 1.873-2.963 4.568-3.691 7.754-2.265-0.245-3.623-0.534-4.166-0.665 1.585-3.27 4.407-5.836 7.856-7.088zM2.614 11.787c0.385 0.104 1.841 0.467 4.549 0.766-0.155 1.107-0.24 2.26-0.24 3.447 0 1.509 0.136 2.96 0.383 4.334-2.325 0.251-3.755 0.552-4.396 0.706-0.607-1.566-0.944-3.264-0.944-5.041 0-1.467 0.228-2.883 0.649-4.213zM3.784 22.886c0.727-0.154 2.029-0.39 3.956-0.591 0.759 2.803 1.993 5.175 3.473 6.874-3.16-1.148-5.79-3.398-7.429-6.282v0zM21.583 28.849c1.195-1.665 2.14-3.907 2.728-6.525 1.982 0.227 3.226 0.494 3.853 0.652-1.5 2.596-3.808 4.669-6.581 5.873z"></path>{' '}
            </g>
          </svg>
          {t(`:enumerations.languages.${defaultLocale.substring(0, 2)}`)}
        </button>

        {nonAutoTranslatableLocales.length > 0 && (
          <>
            <hr />
            <section>
              <strong>{t('switchLanguageModal.manualLanguagesTitle')}</strong>
              {nonAutoTranslatableLocales.map((locale) => (
                <button
                  key={locale.identifier}
                  onClick={() => handleSwitchLanguage(locale.identifier)}
                  className={cc([
                    'ep-settingslayer-language-non-auto-translate',
                    { 'ep-settingslayer-language-switch-item-selected': locale.identifier === selectedShopLocale },
                  ])}
                >
                  {t(`:enumerations.languages.${locale.identifier.substring(0, 2)}`)}
                </button>
              ))}
            </section>
          </>
        )}

        {autoTranslatableLocales.length > 0 && (
          <>
            <hr />
            <section>
              <strong>
                {t('switchLanguageModal.automaticLanguages.title')}
                <Tooltip content={t('switchLanguageModal.automaticLanguages.tooltip')} />
              </strong>
              {autoTranslatableLocales.map((locale) => (
                <button
                  key={locale.identifier}
                  onClick={() => handleSwitchLanguage(locale.identifier)}
                  className={cc([
                    'ep-settingslayer-language-auto-translate',
                    { 'ep-settingslayer-language-switch-item-selected': locale.identifier === selectedShopLocale },
                  ])}
                  // Navigation to automatic translations via the editor is always disabled for now
                  disabled
                >
                  <svg
                    data-token-category="icons"
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="13"
                    viewBox="0 0 16 16"
                    fill="#343434"
                  >
                    <path d="M5.333 3 3 5.333l10.667 10.667 2.333 -2.333 -10.667 -10.667zm1.6 5.133L4.2 5.333 5.333 4.2l2.8 2.8 -1.2 1.133zM10 4.667c0.133 -0.133 0.333 -0.867 0.533 -1.8 0.867 -0.2 1.667 -0.4 1.8 -0.533 -0.133 -0.133 -0.867 -0.333 -1.8 -0.533 -0.2 -0.933 -0.4 -1.667 -0.533 -1.8 -0.133 0.133 -0.333 0.867 -0.533 1.8 -0.867 0.2 -1.667 0.4 -1.8 0.533 0.133 0.133 0.867 0.333 1.8 0.533 0.2 0.933 0.4 1.667 0.533 1.8zM2.667 8.667c-0.133 0.133 -0.333 0.867 -0.533 1.8 -0.867 0.2 -1.667 0.4 -1.8 0.533 0.133 0.133 0.867 0.333 1.8 0.533 0.2 0.933 0.4 1.667 0.533 1.8 0.133 -0.133 0.333 -0.867 0.533 -1.8 0.867 -0.2 1.667 -0.4 1.8 -0.533 -0.133 -0.133 -0.867 -0.333 -1.8 -0.533 -0.2 -0.867 -0.4 -1.667 -0.533 -1.8zm-0.333 -4.667c0.133 -0.133 0.333 -0.8 0.467 -1.533 0.733 -0.133 1.4 -0.333 1.533 -0.467 -0.133 -0.133 -0.8 -0.333 -1.533 -0.467C2.667 0.8 2.467 0.133 2.333 0c-0.133 0.133 -0.333 0.8 -0.467 1.533 -0.733 0.133 -1.4 0.333 -1.533 0.467 0.133 0.133 0.8 0.333 1.533 0.467 0.133 0.733 0.333 1.4 0.467 1.533zM1.333 7.333c0.067 -0.067 0.133 -0.4 0.267 -0.733 0.4 -0.067 0.733 -0.2 0.733 -0.267 -0.067 -0.067 -0.4 -0.133 -0.733 -0.267 -0.133 -0.333 -0.2 -0.667 -0.267 -0.733 -0.067 0.067 -0.133 0.4 -0.267 0.733 -0.333 0.133 -0.667 0.2 -0.733 0.267 0.067 0.067 0.4 0.133 0.733 0.267 0.133 0.333 0.2 0.667 0.267 0.733z" />
                  </svg>
                  {t(`:enumerations.languages.${locale.identifier.substring(0, 2)}`)}
                  <a
                    href={`${shopBaseUrl}/${locale.identifier.substring(0, 2)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ep-form-row-text-link"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M15 3h6v6" />
                      <path d="M10 14 21 3" />
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    </svg>
                  </a>
                </button>
              ))}
            </section>
          </>
        )}
        <hr />
        <div className="ep-form-row ep-settingslayer-language-switch-text">
          <div>
            {withComponents(t)('switchLanguageModal.explanation.paragraph1', {
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 3h6v6" />
                  <path d="M10 14 21 3" />
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                </svg>
              ),
            })}
          </div>
          <div>
            {withComponents(t)('switchLanguageModal.explanation.paragraph2', {
              link: (
                <a
                  href={`${mboBaseUrl}?ViewAction=UnityMBO-ViewLanguages`}
                  target="_parent"
                  className="ep-form-row-text-link"
                >
                  {t('switchLanguageModal.explanation.link')}
                </a>
              ),
            })}
          </div>
        </div>
      </div>
    )
  }
}
